import { Currency, CurrencyAmount, Percent, Token, TokenAmount } from '@etcswapv2/sdk-legacy'
import { useMemo } from 'react'
import { usePublicClient, useWalletClient } from 'wagmi'
import { type Address, encodeFunctionData } from 'viem'
import { ROUTER_ADDRESS } from '../constants'
import { routerAbi } from '../constants/abis'
import { useTransactionAdder } from '../state/transactions/hooks'
import { calculateGasMargin, calculateSlippageAmount } from '../utils'
import { useActiveWeb3React } from './index'
import { getChainById } from '../wagmi'
import { Field } from '../state/burn/actions'
import { ApprovalState } from './useApproveCallback'

export enum RemoveLiquidityCallbackState {
  INVALID,
  LOADING,
  VALID
}

interface SignatureData {
  v: number
  r: string
  s: string
  deadline: number
}

interface RemoveLiquidityCallbackParams {
  currencyA: Currency | undefined
  currencyB: Currency | undefined
  tokenA: Token | undefined
  tokenB: Token | undefined
  parsedAmounts: {
    [Field.LIQUIDITY_PERCENT]: Percent
    [Field.LIQUIDITY]?: TokenAmount
    [Field.CURRENCY_A]?: CurrencyAmount
    [Field.CURRENCY_B]?: CurrencyAmount
  }
  allowedSlippage: number
  deadline: number
  approval: ApprovalState
  signatureData: SignatureData | null
}

export function useRemoveLiquidityCallback({
  currencyA,
  currencyB,
  tokenA,
  tokenB,
  parsedAmounts,
  allowedSlippage,
  deadline,
  approval,
  signatureData
}: RemoveLiquidityCallbackParams): {
  state: RemoveLiquidityCallbackState
  callback: null | (() => Promise<string>)
  error: string | null
} {
  const { account, chainId } = useActiveWeb3React()
  const publicClient = usePublicClient()
  const { data: walletClient } = useWalletClient()

  const addTransaction = useTransactionAdder()

  return useMemo(() => {
    if (!publicClient || !walletClient || !account || !chainId) {
      return { state: RemoveLiquidityCallbackState.INVALID, callback: null, error: 'Missing dependencies' }
    }

    const currencyAmountA = parsedAmounts[Field.CURRENCY_A]
    const currencyAmountB = parsedAmounts[Field.CURRENCY_B]
    const liquidityAmount = parsedAmounts[Field.LIQUIDITY]

    if (!currencyAmountA || !currencyAmountB || !liquidityAmount) {
      return { state: RemoveLiquidityCallbackState.INVALID, callback: null, error: 'Enter amounts' }
    }

    if (!currencyA || !currencyB || !tokenA || !tokenB) {
      return { state: RemoveLiquidityCallbackState.INVALID, callback: null, error: 'Missing tokens' }
    }

    if (approval !== ApprovalState.APPROVED && signatureData === null) {
      return { state: RemoveLiquidityCallbackState.INVALID, callback: null, error: 'Approval required' }
    }

    const routerAddress = ROUTER_ADDRESS[chainId] as Address | undefined
    if (!routerAddress) {
      return { state: RemoveLiquidityCallbackState.INVALID, callback: null, error: 'Router not found for this chain' }
    }

    const chain = getChainById(chainId)
    if (!chain) {
      return { state: RemoveLiquidityCallbackState.INVALID, callback: null, error: 'Unsupported chain' }
    }

    return {
      state: RemoveLiquidityCallbackState.VALID,
      callback: async function onRemove(): Promise<string> {
        const amountsMin = {
          [Field.CURRENCY_A]: calculateSlippageAmount(currencyAmountA, allowedSlippage)[0],
          [Field.CURRENCY_B]: calculateSlippageAmount(currencyAmountB, allowedSlippage)[0]
        }

        // Use property check instead of reference equality to avoid module duplication issues
        const currencyBIsETH = currencyB && !('address' in currencyB)
        const oneCurrencyIsETH = (currencyA && !('address' in currencyA)) || currencyBIsETH
        const deadlineFromNow = Math.ceil(Date.now() / 1000) + deadline

        // Build method calls based on approval type and currency type
        type MethodCall = {
          functionName: string
          args: readonly unknown[]
        }

        const methodCalls: MethodCall[] = []

        if (approval === ApprovalState.APPROVED) {
          // We have approval, use normal remove liquidity
          if (oneCurrencyIsETH) {
            // removeLiquidityETC and removeLiquidityETCSupportingFeeOnTransferTokens
            methodCalls.push({
              functionName: 'removeLiquidityETC',
              args: [
                (currencyBIsETH ? tokenA.address : tokenB.address) as Address,
                BigInt(liquidityAmount.raw.toString()),
                BigInt(amountsMin[currencyBIsETH ? Field.CURRENCY_A : Field.CURRENCY_B].toString()),
                BigInt(amountsMin[currencyBIsETH ? Field.CURRENCY_B : Field.CURRENCY_A].toString()),
                account as Address,
                BigInt(deadlineFromNow)
              ] as const
            })
            methodCalls.push({
              functionName: 'removeLiquidityETCSupportingFeeOnTransferTokens',
              args: [
                (currencyBIsETH ? tokenA.address : tokenB.address) as Address,
                BigInt(liquidityAmount.raw.toString()),
                BigInt(amountsMin[currencyBIsETH ? Field.CURRENCY_A : Field.CURRENCY_B].toString()),
                BigInt(amountsMin[currencyBIsETH ? Field.CURRENCY_B : Field.CURRENCY_A].toString()),
                account as Address,
                BigInt(deadlineFromNow)
              ] as const
            })
          } else {
            // removeLiquidity (token/token)
            methodCalls.push({
              functionName: 'removeLiquidity',
              args: [
                tokenA.address as Address,
                tokenB.address as Address,
                BigInt(liquidityAmount.raw.toString()),
                BigInt(amountsMin[Field.CURRENCY_A].toString()),
                BigInt(amountsMin[Field.CURRENCY_B].toString()),
                account as Address,
                BigInt(deadlineFromNow)
              ] as const
            })
          }
        } else if (signatureData !== null) {
          // We have a signature, use permit versions
          if (oneCurrencyIsETH) {
            // removeLiquidityETCWithPermit and removeLiquidityETCWithPermitSupportingFeeOnTransferTokens
            methodCalls.push({
              functionName: 'removeLiquidityETCWithPermit',
              args: [
                (currencyBIsETH ? tokenA.address : tokenB.address) as Address,
                BigInt(liquidityAmount.raw.toString()),
                BigInt(amountsMin[currencyBIsETH ? Field.CURRENCY_A : Field.CURRENCY_B].toString()),
                BigInt(amountsMin[currencyBIsETH ? Field.CURRENCY_B : Field.CURRENCY_A].toString()),
                account as Address,
                BigInt(signatureData.deadline),
                false, // approveMax
                signatureData.v,
                signatureData.r as `0x${string}`,
                signatureData.s as `0x${string}`
              ] as const
            })
            methodCalls.push({
              functionName: 'removeLiquidityETCWithPermitSupportingFeeOnTransferTokens',
              args: [
                (currencyBIsETH ? tokenA.address : tokenB.address) as Address,
                BigInt(liquidityAmount.raw.toString()),
                BigInt(amountsMin[currencyBIsETH ? Field.CURRENCY_A : Field.CURRENCY_B].toString()),
                BigInt(amountsMin[currencyBIsETH ? Field.CURRENCY_B : Field.CURRENCY_A].toString()),
                account as Address,
                BigInt(signatureData.deadline),
                false, // approveMax
                signatureData.v,
                signatureData.r as `0x${string}`,
                signatureData.s as `0x${string}`
              ] as const
            })
          } else {
            // removeLiquidityWithPermit (token/token)
            methodCalls.push({
              functionName: 'removeLiquidityWithPermit',
              args: [
                tokenA.address as Address,
                tokenB.address as Address,
                BigInt(liquidityAmount.raw.toString()),
                BigInt(amountsMin[Field.CURRENCY_A].toString()),
                BigInt(amountsMin[Field.CURRENCY_B].toString()),
                account as Address,
                BigInt(signatureData.deadline),
                false, // approveMax
                signatureData.v,
                signatureData.r as `0x${string}`,
                signatureData.s as `0x${string}`
              ] as const
            })
          }
        } else {
          throw new Error('Attempting to confirm without approval or a signature. Please contact support.')
        }

        // Try each method call and estimate gas
        const gasEstimates: (bigint | undefined)[] = await Promise.all(
          methodCalls.map(async ({ functionName, args }) => {
            try {
              const data = encodeFunctionData({
                abi: routerAbi,
                functionName: functionName as 'removeLiquidity',
                args: args as readonly [Address, Address, bigint, bigint, bigint, Address, bigint]
              })

              const estimate = await publicClient.estimateGas({
                to: routerAddress,
                data,
                account: account as Address
              })

              return calculateGasMargin(estimate)
            } catch (error) {
              console.error(`estimateGas failed for ${functionName}`, args, error)
              return undefined
            }
          })
        )

        // Find the first successful estimation
        const indexOfSuccessfulEstimation = gasEstimates.findIndex(
          (estimate): estimate is bigint => estimate !== undefined
        )

        if (indexOfSuccessfulEstimation === -1) {
          throw new Error('This transaction would fail. Please contact support.')
        }

        const { functionName, args } = methodCalls[indexOfSuccessfulEstimation]
        const gasLimit = gasEstimates[indexOfSuccessfulEstimation]

        try {
          const data = encodeFunctionData({
            abi: routerAbi,
            functionName: functionName as 'removeLiquidity',
            args: args as readonly [Address, Address, bigint, bigint, bigint, Address, bigint]
          })

          const hash = await walletClient.sendTransaction({
            to: routerAddress,
            data,
            gas: gasLimit,
            chain,
            account: account as Address
          } as unknown as Parameters<typeof walletClient.sendTransaction>[0])

          const summary =
            'Remove ' +
            currencyAmountA.toSignificant(3) +
            ' ' +
            currencyA.symbol +
            ' and ' +
            currencyAmountB.toSignificant(3) +
            ' ' +
            currencyB.symbol

          addTransaction({ hash }, { summary })

          return hash
        } catch (error) {
          // if the user rejected the tx, pass this along
          if (
            (error as { code?: number })?.code === 4001 ||
            (error as Error)?.message?.includes('rejected')
          ) {
            throw new Error('Transaction rejected.')
          } else {
            console.error(`Remove liquidity failed`, error)
            throw new Error(`Remove liquidity failed: ${(error as Error).message}`)
          }
        }
      },
      error: null
    }
  }, [
    publicClient,
    walletClient,
    account,
    chainId,
    currencyA,
    currencyB,
    tokenA,
    tokenB,
    parsedAmounts,
    allowedSlippage,
    deadline,
    approval,
    signatureData,
    addTransaction
  ])
}
